import { httpClient } from "@/lib/http";
import {
  WALLETS_ENDPOINTS,
  TRANSACTIONS_ENDPOINTS,
  PAYMENTS_ENDPOINTS,
  PLATFORM_WALLET_ENDPOINTS,
  PAYOUTS_ENDPOINTS,
} from "@/config/api";
import type { ApiResponse } from "@/lib/http";
import type {
  WalletsQueryParams,
  WalletsResponse,
  WalletDetails,
  CreditWalletRequest,
  CreditWalletResponse,
  DebitWalletRequest,
  DebitWalletResponse,
  FreezeWalletRequest,
  FreezeWalletResponse,
  TransactionsQueryParams,
  TransactionsResponse,
  TransactionDetails,
  PaymentsQueryParams,
  PaymentsResponse,
  PaymentDetails,
  PlatformWallet,
  RetryPayoutResponse,
  CancelPayoutResponse,
} from "../types";

// ============================================================================
// Helper Functions
// ============================================================================

const buildWalletsQuery = (params: WalletsQueryParams): string => {
  const q = new URLSearchParams();
  if (params.page !== undefined) q.append("page", params.page.toString());
  if (params.size !== undefined) q.append("size", params.size.toString());
  if (params.status) q.append("status", params.status);
  if (params.userId) q.append("userId", params.userId);
  if (params.minBalance !== undefined && params.minBalance !== "")
    q.append("minBalance", params.minBalance.toString());
  if (params.maxBalance !== undefined && params.maxBalance !== "")
    q.append("maxBalance", params.maxBalance.toString());
  if (params.userType) q.append("userType", params.userType);
  if (params.search) q.append("search", params.search);
  return q.toString();
};

const buildTransactionsQuery = (params: TransactionsQueryParams): string => {
  const q = new URLSearchParams();
  if (params.page !== undefined) q.append("page", params.page.toString());
  if (params.size !== undefined) q.append("size", params.size.toString());
  if (params.direction) q.append("direction", params.direction);
  if (params.type) q.append("type", params.type);
  if (params.status) q.append("status", params.status);
  if (params.walletId) q.append("walletId", params.walletId);
  if (params.userId) q.append("userId", params.userId);
  if (params.dateFrom) q.append("dateFrom", params.dateFrom);
  if (params.dateTo) q.append("dateTo", params.dateTo);
  if (params.minAmount !== undefined && params.minAmount !== "")
    q.append("minAmount", params.minAmount.toString());
  if (params.maxAmount !== undefined && params.maxAmount !== "")
    q.append("maxAmount", params.maxAmount.toString());
  if (params.search) q.append("search", params.search);
  return q.toString();
};

const buildPaymentsQuery = (params: PaymentsQueryParams): string => {
  const q = new URLSearchParams();
  if (params.page !== undefined) q.append("page", params.page.toString());
  if (params.size !== undefined) q.append("size", params.size.toString());
  if (params.purpose) q.append("purpose", params.purpose);
  if (params.status) q.append("status", params.status);
  if (params.method) q.append("method", params.method);
  if (params.userId) q.append("userId", params.userId);
  if (params.dateFrom) q.append("dateFrom", params.dateFrom);
  if (params.dateTo) q.append("dateTo", params.dateTo);
  if (params.minAmount !== undefined && params.minAmount !== "")
    q.append("minAmount", params.minAmount.toString());
  if (params.maxAmount !== undefined && params.maxAmount !== "")
    q.append("maxAmount", params.maxAmount.toString());
  if (params.search) q.append("search", params.search);
  return q.toString();
};

// ============================================================================
// Wallets API
// ============================================================================

export const getWallets = async (params: WalletsQueryParams = {}) => {
  const qs = buildWalletsQuery(params);
  const url = qs ? `${WALLETS_ENDPOINTS.LIST}?${qs}` : WALLETS_ENDPOINTS.LIST;
  const { data } = await httpClient.get<ApiResponse<WalletsResponse>>(url);
  return data;
};

export const getWalletDetails = async (walletId: string) => {
  const { data } = await httpClient.get<ApiResponse<WalletDetails>>(
    WALLETS_ENDPOINTS.DETAILS(walletId)
  );
  return data;
};

export const creditWallet = async (walletId: string, body: CreditWalletRequest) => {
  const { data } = await httpClient.post<ApiResponse<CreditWalletResponse>>(
    WALLETS_ENDPOINTS.CREDIT(walletId),
    body
  );
  return data;
};

export const debitWallet = async (walletId: string, body: DebitWalletRequest) => {
  const { data } = await httpClient.post<ApiResponse<DebitWalletResponse>>(
    WALLETS_ENDPOINTS.DEBIT(walletId),
    body
  );
  return data;
};

export const freezeWallet = async (walletId: string, body: FreezeWalletRequest) => {
  const { data } = await httpClient.post<ApiResponse<FreezeWalletResponse>>(
    WALLETS_ENDPOINTS.FREEZE(walletId),
    body
  );
  return data;
};

// ============================================================================
// Transactions API
// ============================================================================

export const getTransactions = async (params: TransactionsQueryParams = {}) => {
  const qs = buildTransactionsQuery(params);
  const url = qs ? `${TRANSACTIONS_ENDPOINTS.LIST}?${qs}` : TRANSACTIONS_ENDPOINTS.LIST;
  const { data } = await httpClient.get<ApiResponse<TransactionsResponse>>(url);
  return data;
};

export const getTransactionDetails = async (transactionId: string) => {
  const { data } = await httpClient.get<ApiResponse<TransactionDetails>>(
    TRANSACTIONS_ENDPOINTS.DETAILS(transactionId)
  );
  return data;
};

// ============================================================================
// Payments API
// ============================================================================

export const getPayments = async (params: PaymentsQueryParams = {}) => {
  const qs = buildPaymentsQuery(params);
  const url = qs ? `${PAYMENTS_ENDPOINTS.LIST}?${qs}` : PAYMENTS_ENDPOINTS.LIST;
  const { data } = await httpClient.get<ApiResponse<PaymentsResponse>>(url);
  return data;
};

export const getPaymentDetails = async (paymentId: string) => {
  const { data } = await httpClient.get<ApiResponse<PaymentDetails>>(
    PAYMENTS_ENDPOINTS.DETAILS(paymentId)
  );
  return data;
};

// ============================================================================
// Platform Wallet API
// ============================================================================

export const getPlatformWallet = async () => {
  const { data } = await httpClient.get<ApiResponse<PlatformWallet>>(PLATFORM_WALLET_ENDPOINTS.GET);
  console.log("[platform-wallet] backend response", data);
  return data;
};

// ============================================================================
// Payouts API
// ============================================================================

export const retryPayout = async (payoutId: string) => {
  const { data } = await httpClient.post<ApiResponse<RetryPayoutResponse>>(
    PAYOUTS_ENDPOINTS.RETRY(payoutId)
  );
  return data;
};

export const cancelPayout = async (payoutId: string, reason: string) => {
  const { data } = await httpClient.post<ApiResponse<CancelPayoutResponse>>(
    PAYOUTS_ENDPOINTS.CANCEL(payoutId),
    { reason }
  );
  return data;
};
